import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AppVersionBadge } from "@/components/AppVersionBadge";
import { watchPosition, subscribeNetwork, haptic, isNative } from "@/lib/native";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import StatusPill from "@/components/StatusPill";
import GoogleMapDirections from "@/components/GoogleMapDirections";
import RouteStopsMap from "@/components/RouteStopsMap";
import DriverClockCard from "@/components/DriverClockCard";
import SignaturePad from "@/components/SignaturePad";
import { cacheRoute, readCachedRoute, enqueueAction, readQueue, drainQueue } from "@/lib/offlineStore";
import { MapPin, CheckCircle2, Camera, AlertTriangle, RefreshCw, Truck, Wifi, WifiOff, Navigation, LogOut, Trash, PenLine, Fuel, Sparkles, Clock, PlusCircle, Plus, GripVertical, Map as MapIcon, List as ListIcon, Ban } from "lucide-react";
import { toast } from "sonner";
import { titleCase } from "@/lib/format";
import {
    DndContext, PointerSensor, TouchSensor, KeyboardSensor,
    useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext, verticalListSortingStrategy, useSortable,
    sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Driver() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    // Ref mirror so the WebSocket handler always reads the current user
    // without re-subscribing on every user object change.
    const meRef = useRef(null);
    useEffect(() => { meRef.current = user; }, [user]);
    const [routes, setRoutes] = useState([]);
    const [routeId, setRouteId] = useState("");
    const [route, setRoute] = useState(null);
    const [online, setOnline] = useState(navigator.onLine);
    const [queueLen, setQueueLen] = useState(readQueue().length);
    const [gps, setGps] = useState(null);
    const [gpsError, setGpsError] = useState(null); // "denied" | "unavailable" | "timeout" | null
    const [gpsSplashOpen, setGpsSplashOpen] = useState(false);
    const [sortByNearest, setSortByNearest] = useState(true);
    const [arrivalAlertsEnabled, setArrivalAlertsEnabled] = useState(true);
    const [activeStop, setActiveStop] = useState(null);
    // "list" | "map" — toggled via the header button. Persisted across renders
    // but not across sessions (driver picks fresh each shift).
    const [viewMode, setViewMode] = useState("list");
    // Track which stops have already fired the arrival ding so we don't
    // buzz on every heartbeat. Cleared when a stop is completed or the
    // route changes.
    const arrivedStopIds = useRef(new Set());
    const [fuelDrawer, setFuelDrawer] = useState(false);
    const [fuelForm, setFuelForm] = useState({ amount: "", gallons: "", odometer: "", vendor: "", receipt: null });
    const [fuelSaving, setFuelSaving] = useState(false);
    const [fuelOcrBusy, setFuelOcrBusy] = useState(false);
    const [fuelOcrHint, setFuelOcrHint] = useState(null); // "high"|"medium"|"low"|null
    const fuelFileRef = useRef(null);
    const [photoFiles, setPhotoFiles] = useState([]);        // raw files for upload (up to 5)
    const [photoPreviews, setPhotoPreviews] = useState([]);  // data urls for UI previews
    const [photoUploading, setPhotoUploading] = useState(false);
    const [notes, setNotes] = useState("");
    const [contamination, setContamination] = useState(false);
    const [damagedCart, setDamagedCart] = useState(false);
    const signatureRef = useRef(null);
    const fileInputRef = useRef(null);
    // Quick-photo capture flow (camera icon on stop card). We need a *DOM-attached*
    // <input type="file"> because a detached input node (document.createElement)
    // gets GC'd on iOS Safari + PWAs, silently killing the `change` event so
    // the photo never uploads. `quickPhotoStopRef` holds the target stop while
    // the OS camera picker is open.
    const quickPhotoInputRef = useRef(null);
    const quickPhotoStopRef = useRef(null);

    // GPS watch + stream to backend so dispatch sees live position.
    // Also captures permission errors so we can tell the driver why
    // "nearest me" isn't sorting — the most common cause is the tablet
    // being set to "Block" for this site, which silently kills the watch.
    const requestGps = () => {
        if (!navigator.geolocation) {
            setGpsError("unavailable");
            return null;
        }
        setGpsError(null);
        // Immediate fix — watchPosition can take a while to fire the first
        // callback (especially on a stationary tablet with high-accuracy).
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude,
                          speed: pos.coords.speed, heading: pos.coords.heading });
                setGpsError(null);
            },
            (err) => {
                if (err.code === 1) setGpsError("denied");
                else if (err.code === 2) setGpsError("unavailable");
                else if (err.code === 3) setGpsError("timeout");
                else setGpsError("unavailable");
            },
            { enableHighAccuracy: true, maximumAge: 30000, timeout: 10000 },
        );
        // Continuous watch for movement updates.
        return navigator.geolocation.watchPosition(
            (pos) => {
                setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude,
                          speed: pos.coords.speed, heading: pos.coords.heading });
                setGpsError(null);
            },
            (err) => {
                if (err.code === 1) setGpsError("denied");
                else if (err.code === 2) setGpsError("unavailable");
                else if (err.code === 3) setGpsError("timeout");
            },
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
        );
    };

    useEffect(() => {
        const id = requestGps();
        return () => {
            if (id != null && navigator.geolocation) {
                navigator.geolocation.clearWatch(id);
            }
        };
    }, []);

    // First-run splash: if we can't get a GPS fix within 4 seconds AND the
    // driver hasn't already dismissed this splash on this device, show a
    // one-time explainer with an "Allow location" button. Prevents new
    // hires from silently tapping "Block" on the browser prompt.
    useEffect(() => {
        if (!navigator.geolocation) return;
        if (typeof window === "undefined") return;
        const dismissed = window.localStorage?.getItem("rd_gps_splash_dismissed") === "1";
        if (dismissed) return;
        const t = setTimeout(() => {
            // Only surface the splash if we STILL don't have a fix and the
            // permission is denied OR still 'prompt' (i.e. driver hasn't
            // decided yet). Skip if the driver has an active fix.
            if (gps) return;
            if (gpsError === "denied" || gpsError === "timeout" || gpsError === null) {
                setGpsSplashOpen(true);
            }
        }, 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, []);

    const acceptGpsSplash = () => {
        window.localStorage?.setItem("rd_gps_splash_dismissed", "1");
        setGpsSplashOpen(false);
        requestGps();
    };
    const dismissGpsSplash = () => {
        window.localStorage?.setItem("rd_gps_splash_dismissed", "1");
        setGpsSplashOpen(false);
    };

    // Stream GPS to backend every 12 seconds while a route is active
    useEffect(() => {
        if (!gps || !route?.truck_id || !online) return;
        const send = () => {
            api.post(`/fleet/trucks/${route.truck_id}/location`, {
                lat: gps.lat, lng: gps.lng,
                speed: gps.speed, heading: gps.heading,
                route_id: route.id, driver_id: user?.id,
            }).catch(() => {});
        };
        send();
        const t = setInterval(send, 12000);
        return () => clearInterval(t);
    }, [gps, route?.truck_id, route?.id, online, user?.id]);

    // Online status
    useEffect(() => {
        const go = () => setOnline(true);
        const gone = () => setOnline(false);
        window.addEventListener("online", go);
        window.addEventListener("offline", gone);
        return () => {
            window.removeEventListener("online", go);
            window.removeEventListener("offline", gone);
        };
    }, []);

    // Load routes — try today first, fall back to any route with stops
    const load = useCallback(async () => {
        const today = DAYS[new Date().getDay()];
        try {
            // Fetch every route so we can offer a picker if today has none.
            const { data: all } = await api.get(`/routes`);
            const todayRoutes = all.filter((r) => (r.day || "").toLowerCase() === today);
            const list = todayRoutes.length > 0 ? todayRoutes : all;
            setRoutes(list);
            const initial = todayRoutes[0] || all[0];
            if (initial && !routeId) {
                setRouteId(initial.id);
                setRoute(initial);
                cacheRoute(initial);
                // Fire-and-forget ETA fetch — non-blocking
                api.get(`/routes/${initial.id}/etas`).then(({ data: eta }) => {
                    if (eta.stops) setRoute((prev) => (prev?.id === initial.id ? { ...prev, stops: eta.stops } : prev));
                }).catch(() => {});
                // Silently backfill lat/lng for stops that lack coords —
                // otherwise "nearest me" sort can't compute distance for
                // them and they all sink to the bottom. Only fires if
                // there's actually work to do.
                const missing = (initial.stops || []).some(
                    (s) => s.lat == null || s.lng == null);
                if (missing) {
                    api.post(`/routes/${initial.id}/backfill-coords`)
                        .then(({ data: fix }) => {
                            if (fix?.filled > 0) {
                                api.get(`/routes/${initial.id}`).then(({ data: fresh }) => {
                                    setRoute((prev) => (prev?.id === initial.id ? fresh : prev));
                                    cacheRoute(fresh);
                                }).catch(() => {});
                            }
                        }).catch(() => {});
                }
            }
        } catch {
            // Offline — try cache
            const cached = readCachedRoute();
            if (cached) {
                setRoute(cached.route);
                setRoutes([cached.route]);
                toast.info("Showing cached route (offline)");
            }
        }
    }, [routeId]);

    useEffect(() => { load(); }, [load]);

    // Live route-updates: reconnect + auto-reload when dispatch publishes.
    useEffect(() => {
        const url = process.env.REACT_APP_BACKEND_URL;
        if (!url) return;
        const wsProto = url.startsWith("https") ? "wss" : "ws";
        const wsUrl = url.replace(/^https?/, wsProto) + "/api/ws/dispatch";
        let ws = null;
        let reconnectTimer = null;
        let closed = false;

        const connect = () => {
            try {
                ws = new WebSocket(wsUrl);
                ws.onmessage = (ev) => {
                    try {
                        const msg = JSON.parse(ev.data);
                        if (msg.type === "route.published") {
                            // If it's my active route (or I don't have one), reload.
                            if (!routeId || msg.route_id === routeId
                                || (route && msg.day === route.day)) {
                                toast.success(
                                    `Dispatch updated the route (${msg.stop_count} stops) — reloading…`,
                                    { duration: 4000 }
                                );
                                load();
                            }
                        } else if (msg.type === "route.stop_updated"
                                   && msg.route_id === routeId
                                   && msg.stop
                                   && msg.actor_id !== meRef.current?.id) {
                            // Co-driver on the same route just changed a stop.
                            // Merge the full updated stop into local state so
                            // this driver sees the completion (or re-open,
                            // photo attach, etc.) without a manual refresh.
                            setRoute((prev) => {
                                if (!prev || prev.id !== msg.route_id) return prev;
                                const merged = {
                                    ...prev,
                                    stops: prev.stops.map((s) =>
                                        s.id === msg.stop_id ? { ...s, ...msg.stop } : s
                                    ),
                                };
                                cacheRoute(merged);
                                return merged;
                            });
                            // Friendly heads-up so drivers know who did what.
                            if (msg.stop_status === "completed" && msg.actor_name) {
                                const addr = msg.stop.address || "a stop";
                                toast.info(`${msg.actor_name} completed ${addr}`,
                                           { duration: 3500 });
                            }
                        }
                    } catch { /* ignore malformed */ }
                };
                ws.onclose = () => {
                    if (closed) return;
                    reconnectTimer = setTimeout(connect, 5000);
                };
                ws.onerror = () => { try { ws.close(); } catch {} };
            } catch {
                reconnectTimer = setTimeout(connect, 5000);
            }
        };
        connect();
        return () => {
            closed = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            try { ws && ws.close(); } catch {}
        };
    }, [routeId, route?.day, load]);

    const pickRoute = async (id) => {
        setRouteId(id);
        const r = routes.find((x) => x.id === id);
        setRoute(r);
        if (r) cacheRoute(r);
        fetchEtas(id);
        // Backfill coords silently for the newly-selected route (same
        // as initial load — handles the case where the driver switches
        // from Kenworth to Automated).
        if (r && (r.stops || []).some((s) => s.lat == null || s.lng == null)) {
            api.post(`/routes/${id}/backfill-coords`)
                .then(({ data: fix }) => {
                    if (fix?.filled > 0) {
                        api.get(`/routes/${id}`).then(({ data: fresh }) => {
                            setRoute((prev) => (prev?.id === id ? fresh : prev));
                            cacheRoute(fresh);
                        }).catch(() => {});
                    }
                }).catch(() => {});
        }
    };

    const fetchEtas = async (id) => {
        if (!online || !id) return;
        try {
            const { data } = await api.get(`/routes/${id}/etas`);
            if (data.stops) {
                setRoute((prev) => (prev && prev.id === id ? { ...prev, stops: data.stops } : prev));
            }
        } catch { /* silent — driver can work without ETAs */ }
    };

    // Drain queue when back online. Also retry every 30s so stuck items
    // don't linger — the driver never has to think about it.
    const [draining, setDraining] = useState(false);
    const drainNow = async () => {
        if (draining) return;
        setDraining(true);
        try {
            const n = await drainQueue(api, () => setQueueLen(readQueue().length));
            setQueueLen(readQueue().length);
            if (n > 0) {
                toast.success(`Synced ${n} queued action${n > 1 ? "s" : ""}`);
                await load();
            }
        } finally {
            setDraining(false);
        }
    };
    useEffect(() => {
        if (!online) return;
        drainNow();
        const t = setInterval(() => {
            // Only bother if there's actually anything queued
            if (readQueue().length > 0) drainNow();
        }, 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [online]);

    const resetStopForm = () => {
        setActiveStop(null);
        setPhotoFiles([]);
        setPhotoPreviews([]);
        setNotes("");
        setContamination(false);
        setDamagedCart(false);
        signatureRef.current?.clear();
    };

    /** Upload all captured photos + signature (if any) and return URLs.
     *  Returns ``photo_urls`` (array) plus ``photo_url`` = first for
     *  backward-compat with the completion email + legacy reports. */
    const uploadAttachments = async () => {
        const result = {
            photo_url: null, photo_urls: [], photo_file_ids: [],
            signature_url: null,
        };
        if (!online) {
            // Offline — keep data URLs locally so the sheet renders, but they
            // won't actually upload. Skip remote storage.
            if (photoPreviews.length) {
                result.photo_urls = [...photoPreviews];
                result.photo_url = photoPreviews[0];
            }
            const sig = signatureRef.current?.getDataUrl?.();
            if (sig) result.signature_url = sig;
            return result;
        }
        setPhotoUploading(true);
        try {
            for (const file of photoFiles) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads?kind=route-photo", fd);
                result.photo_file_ids.push(data.id);
                result.photo_urls.push(`/api/uploads/${data.id}`);
            }
            result.photo_url = result.photo_urls[0] || null;
            const sig = signatureRef.current?.getDataUrl?.();
            if (sig) {
                const blob = dataUrlToBlob(sig);
                const fd = new FormData();
                fd.append("file", blob, "signature.png");
                const { data } = await api.post("/uploads?kind=signature", fd);
                result.signature_url = `/api/uploads/${data.id}`;
            }
        } catch (e) {
            toast.error("Could not upload attachments — saved offline");
        } finally {
            setPhotoUploading(false);
        }
        return result;
    };

    const logExtraCharge = async (stop) => {
        if (!stop.customer_id) {
            toast.error("No customer linked to this stop");
            return;
        }
        const desc = window.prompt(
            `Extra charge for ${stop.customer_name}\n\nWhat's the fee for? (Extra bag, bulk pickup, contamination, etc.)`,
            "Extra bag",
        );
        if (!desc || !desc.trim()) return;
        const amt = window.prompt("Amount ($)?", "5");
        const amount = parseFloat(amt || 0);
        if (!amount || amount <= 0) {
            toast.error("Amount must be greater than zero");
            return;
        }
        try {
            await api.post(`/customers/${stop.customer_id}/pending-charges`, {
                description: desc.trim(), amount,
            });
            toast.success(`$${amount.toFixed(2)} logged — office will add it to next invoice`);
            haptic?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save charge");
        }
    };

    const markCartNotOut = async (stop) => {
        const patch = {
            status: "cart_not_out",
            notes: notes || "Cart was not out at the curb",
        };
        if (online) {
            try {
                await api.patch(`/routes/${route.id}/stops/${stop.id}`, patch);
                toast.success("Marked cart-not-out");
            } catch (err) {
                if (err?.isAuthError) return;
                enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
                toast.info("Saved offline — will sync when back online");
            }
        } else {
            enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
            toast.info("Saved offline — will sync when back online");
        }
        const next = {
            ...route,
            stops: route.stops.map((s) => s.id === stop.id
                ? { ...s, status: "cart_not_out" } : s),
        };
        setRoute(next);
        cacheRoute(next);
        setQueueLen(readQueue().length);
        resetStopForm();
    };

    const [dumpDialogOpen, setDumpDialogOpen] = useState(false);
    const [dumpWeight, setDumpWeight] = useState("");
    const [dumpCost, setDumpCost] = useState("");
    const [dumpFacility, setDumpFacility] = useState("");
    const recordDumpLoad = async () => {
        if (!route?.id) return;
        const w = parseFloat(dumpWeight);
        const c = parseFloat(dumpCost);
        if (!w || w <= 0) { toast.error("Weight in tons must be greater than 0"); return; }
        if (isNaN(c) || c < 0) { toast.error("Cost must be zero or positive"); return; }
        try {
            await api.post(`/routes/${route.id}/dump-load`, {
                weight_tons: w, cost: c,
                facility: dumpFacility.trim(),
            });
            toast.success(`Dump load recorded: ${w}t · $${c.toFixed(2)}`);
            setDumpWeight(""); setDumpCost(""); setDumpFacility("");
            setDumpDialogOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const finishRoute = async () => {
        if (!route?.id) return;
        if (!window.confirm(
            "End this route?\n\nThis emails admin/dispatch a summary of every stop, " +
            "every extra fee you logged, and every dump load recorded today. " +
            "All extras will roll into the customers' next invoices."
        )) return;
        try {
            const { data } = await api.post(`/routes/${route.id}/route-complete-summary`);
            toast.success(
                `Route complete — ${data.completed_stops} stops · ` +
                `$${data.charges_total.toFixed(2)} extras · ` +
                `emailed to ${data.emailed_to.length} admin(s)`,
                { duration: 6000 }
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send summary failed");
        }
    };

    const uncompleteStop = async (stop) => {
        if (!window.confirm(
            `Undo completion for ${stop.customer_name}?\n\n` +
            `The stop will go back to Pending so you (or another driver) can service it.`
        )) return;
        try {
            await api.patch(`/routes/${route.id}/stops/${stop.id}`, {
                status: "pending",
                completed_at: null,
            });
            const next = {
                ...route,
                stops: route.stops.map((s) => s.id === stop.id
                    ? { ...s, status: "pending", completed_at: null } : s),
            };
            setRoute(next);
            cacheRoute(next);
            toast.success("Stop moved back to Pending");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Undo failed");
        }
    };

    /** Upload a picked File to a specific stop. Extracted from the input handler
     *  so both the quick-camera flow (stop card) and any future entry point can
     *  share the exact same online + offline queue paths.
     *
     *  Appends to ``stop.photo_urls`` — the FIRST photo is mirrored onto
     *  ``photo_url`` for backward compatibility (customer completion email,
     *  route reports, timeline queries).
     */
    const uploadStopPhoto = async (stop, file) => {
        if (!stop || !file || !route) return;
        // Read the file as a data URL now — this is BOTH what we show in the
        // local preview AND what we queue if we're offline, so photos never
        // get lost on a bad-signal stop.
        const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        // Grab the freshest photo_urls from state so concurrent uploads don't
        // stomp each other.
        const currentStop = (route.stops || []).find((s) => s.id === stop.id) || stop;
        const existingUrls = [
            ...(currentStop.photo_urls || []),
        ];
        if (currentStop.photo_url && !existingUrls.includes(currentStop.photo_url)) {
            existingUrls.unshift(currentStop.photo_url);
        }
        const tryOnline = async () => {
            const fd = new FormData();
            fd.append("file", file);
            const { data: upload } = await api.post("/uploads?kind=stop_photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = `/api/uploads/${upload.id}`;
            const nextUrls = [...existingUrls, url];
            await api.patch(`/routes/${route.id}/stops/${stop.id}`, {
                photo_urls: nextUrls,
                photo_url: nextUrls[0],
            });
            return { photoUrl: url, photoUrls: nextUrls };
        };
        let photoUrl = dataUrl;
        let photoUrls = [...existingUrls, dataUrl];
        let queued = false;
        if (navigator.onLine) {
            try {
                const res = await tryOnline();
                photoUrl = res.photoUrl;
                photoUrls = res.photoUrls;
            } catch (err) {
                if (err?.isAuthError) return;
                queued = true;
                enqueueAction({
                    type: "stop_photo",
                    route_id: route.id, stop_id: stop.id,
                    photo_data_url: dataUrl,
                    photo_filename: file.name || "cart.jpg",
                });
                toast.warning("Photo saved offline — will sync when back online");
            }
        } else {
            queued = true;
            enqueueAction({
                type: "stop_photo",
                route_id: route.id, stop_id: stop.id,
                photo_data_url: dataUrl,
                photo_filename: file.name || "cart.jpg",
            });
        }
        // Merge into the *latest* route state (avoids clobbering concurrent
        // websocket updates that arrived while the upload was in flight).
        setRoute((prev) => {
            if (!prev || prev.id !== route.id) return prev;
            const next = {
                ...prev,
                stops: prev.stops.map((s) => s.id === stop.id
                    ? { ...s, photo_url: photoUrls[0] || photoUrl, photo_urls: photoUrls } : s),
            };
            cacheRoute(next);
            return next;
        });
        setQueueLen(readQueue().length);
        if (!queued) {
            toast.success("Photo attached — tap Complete when done");
        }
    };

    const attachPhotoToStop = (stop) => {
        // Trigger the *persistent* DOM-attached input. Store the target stop
        // in a ref so the onChange handler knows where to attach the file.
        quickPhotoStopRef.current = stop;
        const el = quickPhotoInputRef.current;
        if (!el) return;
        // Reset value so picking the same file twice still fires change.
        try { el.value = ""; } catch { /* readonly on some UAs */ }
        el.click();
    };

    const handleQuickPhotoChange = async (e) => {
        const files = Array.from(e.target.files || []);
        const stop = quickPhotoStopRef.current;
        quickPhotoStopRef.current = null;
        if (!files.length || !stop) return;
        // Cap at 5 photos per stop to protect PWA localStorage when offline.
        const capped = files.slice(0, 5);
        for (const file of capped) {
            try {
                // Sequential so uploadStopPhoto's optimistic state merge
                // sees the previous photo's URL when computing the next
                // photo_urls array.
                await uploadStopPhoto(stop, file);
            } catch (err) {
                toast.error("Photo save failed — please try again");
                console.error("quick photo attach failed", err);
            }
        }
        if (files.length > 5) {
            toast.info(`Only the first 5 photos were attached — added ${capped.length}.`);
        }
    };

    const markStopComplete = async (stop) => {
        const att = await uploadAttachments();
        // Preserve any photos the driver already attached via the Camera
        // button on the stop card — uploadAttachments() only returns photos
        // when the ACTIVE-STOP sheet camera was used.
        const existingUrls = Array.isArray(stop.photo_urls) ? stop.photo_urls : [];
        const primary = att.photo_url || stop.photo_url || existingUrls[0] || null;
        const mergedUrls = [...existingUrls];
        if (stop.photo_url && !mergedUrls.includes(stop.photo_url)) {
            mergedUrls.unshift(stop.photo_url);
        }
        for (const u of att.photo_urls || []) {
            if (u && !mergedUrls.includes(u)) mergedUrls.push(u);
        }
        const patch = {
            status: "completed",
            notes,
            photo_url: primary,
            photo_urls: mergedUrls,
            signature_url: att.signature_url,
            contamination: contamination || undefined,
            damaged_cart: damagedCart || undefined,
        };
        if (online) {
            try {
                await api.patch(`/routes/${route.id}/stops/${stop.id}`, patch);
                toast.success("Stop completed");
            } catch (err) {
                if (err?.isAuthError) {
                    // 401/403 handled globally in api.js — do NOT queue,
                    // because "syncing later" will just replay the same
                    // auth failure. Fall through and skip the local state
                    // update so the stop stays pending until re-login.
                    return;
                }
                enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
                toast.info("Saved offline — will sync when back online");
            }
        } else {
            enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
            toast.info("Saved offline — will sync when back online");
        }
        const next = {
            ...route,
            stops: route.stops.map((s) => s.id === stop.id ? { ...s, status: "completed" } : s),
        };
        setRoute(next);
        cacheRoute(next);
        setQueueLen(readQueue().length);
        resetStopForm();
    };

    const markMissed = async (stop) => {
        const att = await uploadAttachments();
        const existingUrls = Array.isArray(stop.photo_urls) ? stop.photo_urls : [];
        const primary = att.photo_url || stop.photo_url || existingUrls[0] || null;
        const mergedUrls = [...existingUrls];
        if (stop.photo_url && !mergedUrls.includes(stop.photo_url)) {
            mergedUrls.unshift(stop.photo_url);
        }
        for (const u of att.photo_urls || []) {
            if (u && !mergedUrls.includes(u)) mergedUrls.push(u);
        }
        const patch = {
            status: "missed", notes,
            photo_url: primary,
            photo_urls: mergedUrls,
            signature_url: att.signature_url,
            contamination: contamination || undefined,
            damaged_cart: damagedCart || undefined,
        };
        if (online) {
            try { await api.patch(`/routes/${route.id}/stops/${stop.id}`, patch); }
            catch (err) {
                if (err?.isAuthError) return;
                enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
            }
        } else {
            enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
        }
        const next = { ...route, stops: route.stops.map((s) => s.id === stop.id ? { ...s, status: "missed" } : s) };
        setRoute(next);
        cacheRoute(next);
        resetStopForm();
        toast(stop.customer_name + " marked missed");
    };

    /** Driver rejects a stop (e.g. bags outside cart, wrong cart, blocked).
     *  Uses the notes textarea as the mandatory rejection reason so the
     *  office knows why we didn't pick it up. Counts toward "done" work
     *  (no re-attempt) but shows separately from Completed in reports. */
    const markRejected = async (stop) => {
        const reason = (notes || "").trim();
        if (!reason) {
            toast.error("Add a reason in Notes before rejecting the pickup");
            return;
        }
        const att = await uploadAttachments();
        const existingUrls = Array.isArray(stop.photo_urls) ? stop.photo_urls : [];
        const primary = att.photo_url || stop.photo_url || existingUrls[0] || null;
        const mergedUrls = [...existingUrls];
        if (stop.photo_url && !mergedUrls.includes(stop.photo_url)) {
            mergedUrls.unshift(stop.photo_url);
        }
        for (const u of att.photo_urls || []) {
            if (u && !mergedUrls.includes(u)) mergedUrls.push(u);
        }
        const patch = {
            status: "rejected",
            notes: reason,
            rejection_reason: reason,
            photo_url: primary,
            photo_urls: mergedUrls,
            signature_url: att.signature_url,
            contamination: contamination || undefined,
            damaged_cart: damagedCart || undefined,
        };
        if (online) {
            try { await api.patch(`/routes/${route.id}/stops/${stop.id}`, patch); }
            catch (err) {
                if (err?.isAuthError) return;
                enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
                toast.info("Saved offline — will sync when back online");
            }
        } else {
            enqueueAction({ type: "stop_complete", route_id: route.id, stop_id: stop.id, ...patch });
            toast.info("Saved offline — will sync when back online");
        }
        const next = {
            ...route,
            stops: route.stops.map((s) => s.id === stop.id
                ? { ...s, status: "rejected", rejection_reason: reason } : s),
        };
        setRoute(next);
        cacheRoute(next);
        setQueueLen(readQueue().length);
        resetStopForm();
        toast.warning(`${stop.customer_name} — pickup rejected`);
    };

    const handlePhoto = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        // Cap the total at 5 photos (existing + new) — protects offline
        // localStorage on the PWA.
        const availableSlots = Math.max(0, 5 - photoFiles.length);
        const taking = files.slice(0, availableSlots);
        if (files.length > availableSlots) {
            toast.info(`Only ${availableSlots} more photo${availableSlots === 1 ? "" : "s"} allowed for this stop`);
        }
        setPhotoFiles((prev) => [...prev, ...taking]);
        // Read each file as data URL for previewing.
        taking.forEach((f) => {
            const reader = new FileReader();
            reader.onload = (ev) => setPhotoPreviews((prev) => [...prev, ev.target.result]);
            reader.readAsDataURL(f);
        });
        // Reset the input value so picking the same file twice still fires.
        try { e.target.value = ""; } catch { /* readonly on some UAs */ }
    };

    const clearPhotoAt = (idx) => {
        setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
        setPhotoPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const runFuelOcr = async (file) => {
        setFuelOcrBusy(true);
        setFuelOcrHint(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/expenses/parse-receipt", fd);
            // Only overwrite empty fields — never trample driver-entered values
            setFuelForm((f) => ({
                ...f,
                amount: f.amount || (data.total_amount != null ? String(data.total_amount) : ""),
                gallons: f.gallons || (data.gallons != null ? String(data.gallons) : ""),
                odometer: f.odometer || (data.odometer != null ? String(data.odometer) : ""),
                vendor: f.vendor || data.vendor || "",
                receipt: file,
            }));
            setFuelOcrHint(data.confidence || "low");
            const readable = data.total_amount != null || data.gallons != null || data.vendor;
            if (readable) toast.success(`Auto-filled from receipt (${data.confidence})`);
            else toast.info("Receipt captured — please fill in the details");
        } catch (e) {
            toast.info("Receipt saved — please fill in the details");
            setFuelForm((f) => ({ ...f, receipt: file }));
        } finally {
            setFuelOcrBusy(false);
        }
    };

    const submitFuel = async () => {
        if (!fuelForm.amount || Number(fuelForm.amount) <= 0) {
            return toast.error("Enter fuel amount ($)");
        }
        if (!online) {
            return toast.error("Fuel entry requires internet — try again once you reconnect");
        }
        setFuelSaving(true);
        try {
            let receipt_file_id = null;
            if (fuelForm.receipt) {
                const fd = new FormData();
                fd.append("file", fuelForm.receipt);
                const { data } = await api.post("/uploads?kind=receipt", fd);
                receipt_file_id = data.id;
            }
            await api.post("/expenses", {
                category: "fuel",
                amount: Number(fuelForm.amount),
                gallons: fuelForm.gallons ? Number(fuelForm.gallons) : null,
                odometer: fuelForm.odometer ? Number(fuelForm.odometer) : null,
                vendor: fuelForm.vendor,
                truck_id: route?.truck_id || null,
                receipt_file_id,
                description: `Fill-up${fuelForm.gallons ? ` (${fuelForm.gallons} gal)` : ""}`,
            });
            toast.success("Fuel logged");
            setFuelDrawer(false);
            setFuelForm({ amount: "", gallons: "", odometer: "", vendor: "", receipt: null });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save");
        } finally { setFuelSaving(false); }
    };

    const handleLogout = async () => { await logout(); nav("/login"); };

    const [driverSearch, setDriverSearch] = useState("");

    const pending = route?.stops?.filter((s) => s.status === "pending") || [];
    const completed = route?.stops?.filter((s) => s.status === "completed") || [];
    const missed = route?.stops?.filter((s) => s.status === "missed") || [];
    const rejected = route?.stops?.filter((s) => s.status === "rejected") || [];
    const progressPct = route?.stops?.length
        ? Math.round(((completed.length + rejected.length) / route.stops.length) * 100)
        : 0;

    // Filter helper used to hide non-matching stops when driver is searching.
    const matchesSearch = (s) => {
        const q = driverSearch.trim().toLowerCase();
        if (!q) return true;
        return (s.customer_name || "").toLowerCase().includes(q)
            || (s.address || "").toLowerCase().includes(q);
    };

    // Haversine distance in miles between two lat/lng points.
    const distanceMiles = (a, b) => {
        if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
        const R = 3958.8;
        const toRad = (deg) => (deg * Math.PI) / 180;
        const dLat = toRad(b.lat - a.lat);
        const dLng = toRad(b.lng - a.lng);
        const s = Math.sin(dLat / 2) ** 2 +
                  Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(s));
    };

    // Precompute pending stops with a distance field. Stops that don't have
    // coords fall to the bottom in stable order so drivers can still see them.
    const gpsAvailable = gps && gps.lat != null && gps.lng != null;
    const pendingWithDistance = pending.map((s) => ({
        ...s,
        _distance: gpsAvailable ? distanceMiles(gps, s) : null,
    }));
    const displayPending = ((sortByNearest && gpsAvailable)
        ? [...pendingWithDistance].sort((a, b) => {
            if (a._distance == null && b._distance == null) return 0;
            if (a._distance == null) return 1;
            if (b._distance == null) return -1;
            return a._distance - b._distance;
          })
        : pendingWithDistance).filter(matchesSearch);

    // Arrival alert — buzz + double-chime once when the nearest pending stop
    // drops inside 200 ft (allows for the truck's stopping distance). Fires
    // at most once per stop per session; resets automatically when the stop
    // is marked complete or the driver picks a different route.
    const ARRIVAL_THRESHOLD_MI = 200 / 5280;  // 200 feet
    useEffect(() => {
        if (!arrivalAlertsEnabled) return;
        const near = displayPending.find(
            (s) => s._distance != null && s._distance <= ARRIVAL_THRESHOLD_MI
        );
        if (!near) return;
        if (arrivedStopIds.current.has(near.id)) return;
        arrivedStopIds.current.add(near.id);
        // Haptic pulse — uses native Capacitor Haptics API when available
        // (crisper on iOS than the Web Vibration API), Web Vibration on
        // browsers, silent on iOS Safari (which blocks navigator.vibrate).
        haptic("arrival");
        // Two short beeps via Web Audio (no asset needed, works fully offline).
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const beep = (startAt, freq = 880) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "sine";
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
                gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + startAt + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + 0.28);
                osc.connect(gain).connect(ctx.destination);
                osc.start(ctx.currentTime + startAt);
                osc.stop(ctx.currentTime + startAt + 0.3);
            };
            beep(0, 880);   // A5
            beep(0.32, 1175); // D6 — pleasant rising two-tone
        } catch { /* AudioContext may fail before first user gesture */ }
        toast.info(`Arrived: ${near.customer_name}`, { duration: 3500 });
    }, [displayPending, arrivalAlertsEnabled, ARRIVAL_THRESHOLD_MI]);

    // Reset arrival tracking when the driver picks a different route.
    useEffect(() => {
        arrivedStopIds.current = new Set();
    }, [routeId]);

    // -------- Drag-to-reorder (only when NOT sorting by nearest) --------
    // Use activation constraints so a normal tap on the Complete button
    // doesn't turn into a drag: pointer needs 8px move, touch needs 250ms
    // long-press.
    const dragSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 250, tolerance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const canReorder = !sortByNearest && !driverSearch.trim();

    const persistReorder = async (orderedIds) => {
        if (!route) return;
        try {
            await api.post(`/routes/${route.id}/reorder`, {
                ordered_stop_ids: orderedIds,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reorder failed — pulled from server");
            load();
        }
    };

    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id || !route) return;
        const pendingIds = displayPending.map((s) => s.id);
        const from = pendingIds.indexOf(active.id);
        const to = pendingIds.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const newPendingOrder = arrayMove(pendingIds, from, to);
        // Rebuild the full stop list preserving completed/missed positions
        // relative to each other.
        const byId = Object.fromEntries((route.stops || []).map((s) => [s.id, s]));
        const nonPending = (route.stops || []).filter((s) => s.status !== "pending");
        const newPendingStops = newPendingOrder.map((id) => byId[id]).filter(Boolean);
        const newStops = [...newPendingStops, ...nonPending].map((s, i) => ({
            ...s, sequence: i,
        }));
        const next = { ...route, stops: newStops };
        setRoute(next);
        cacheRoute(next);
        haptic("light");
        persistReorder([...newPendingOrder, ...nonPending.map((s) => s.id)]);
    };
    // --------------------------------------------------------------------

    return (
        <div className="min-h-screen bg-background" data-testid="driver-page">
            {gpsSplashOpen && (
                <div
                    data-testid="driver-gps-splash"
                    className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-4"
                >
                    <div className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 space-y-4 shadow-2xl">
                        <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                            <Navigation className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="text-center space-y-2">
                            <h2 className="font-heading text-xl font-bold">Turn on location for this route</h2>
                            <p className="text-sm text-muted-foreground">
                                WasteHQ uses your location to suggest the closest stop,
                                keep dispatch updated, and record proof-of-service for each pickup.
                            </p>
                            {gpsError === "denied" && (
                                <p className="text-xs text-red-600">
                                    Your browser blocked location earlier. Tap Allow, then choose
                                    "Allow" when the browser asks.
                                </p>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 pt-2">
                            <Button
                                onClick={acceptGpsSplash}
                                data-testid="driver-gps-splash-allow"
                            >
                                <Navigation className="w-4 h-4 mr-2" />
                                Allow location
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={dismissGpsSplash}
                                data-testid="driver-gps-splash-dismiss"
                            >
                                Not now
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Persistent hidden file input for the quick-camera flow on stop
                cards. MUST stay in the DOM — a dynamically-created input
                gets garbage-collected on iOS Safari + PWAs before its
                `change` event can fire, silently dropping the photo. */}
            <input
                ref={quickPhotoInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleQuickPhotoChange}
                style={{ display: "none" }}
                data-testid="driver-quick-photo-input"
            />
            {/* Header — clears iPhone notch / Android status bar via safe-area-inset */}
            <header
                className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border"
                style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
            >
                <div className="px-4 pt-3 pb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <Truck className="w-5 h-5 text-primary shrink-0" />
                        <div className="min-w-0">
                            <div className="font-heading font-bold text-sm truncate">{user?.name || "Driver"}</div>
                            <div className="overline text-[9px]">Driver app</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={queueLen > 0 ? () => drainNow() : undefined}
                            disabled={queueLen === 0 || draining}
                            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-full ${
                                online ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                       : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                            } ${queueLen > 0 ? "cursor-pointer hover:brightness-110" : "cursor-default"}`}
                            data-testid="driver-online-pill"
                            title={queueLen > 0 ? "Tap to retry sync now" : ""}
                        >
                            {online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                            {online ? "Online" : "Offline"}
                            {queueLen > 0 && (
                                <span className="font-mono">
                                    · {draining ? "syncing…" : `${queueLen} queued · retry`}
                                </span>
                            )}
                        </button>
                        <Button variant="outline" size="sm" onClick={() => setFuelDrawer(true)}
                                data-testid="driver-fuel-btn">
                            <Fuel className="w-4 h-4 mr-1" />Fuel
                        </Button>
                        <Button variant="ghost" size="icon" onClick={handleLogout}
                                className="h-11 w-11" data-testid="driver-logout">
                            <LogOut className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* Route picker + progress */}
                <div className="px-4 pb-3 space-y-2">
                    {/* Clock in/out card — always visible so driver_helpers
                        (who never see a route) can still punch. Renders
                        nothing for admins / dispatchers. */}
                    <DriverClockCard />
                    <Select value={routeId} onValueChange={pickRoute}>
                        <SelectTrigger className="w-full" data-testid="driver-route-select">
                            <SelectValue placeholder="Pick a route to run" />
                        </SelectTrigger>
                        <SelectContent>
                            {routes.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {r.name} · {titleCase(r.day || "")} · {r.stops?.length || 0} stops
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {routes.length === 0 && (
                        <div className="text-xs text-muted-foreground bg-secondary/50 rounded p-3 leading-relaxed"
                             data-testid="driver-no-routes-banner">
                            No routes yet. Ask dispatch to <strong>Generate</strong> a route for
                            your pickup day on the Routing page.
                        </div>
                    )}
                    {routes.length > 0 && route
                        && (route.day || "").toLowerCase() !== DAYS[new Date().getDay()] && (
                        <div className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900 rounded p-2"
                             data-testid="driver-day-mismatch-banner">
                            No route was generated for today ({titleCase(DAYS[new Date().getDay()])}).
                            Showing {titleCase(route.day)}'s route — verify with dispatch before running.
                        </div>
                    )}
                    {route && (
                        <div>
                            <div className="flex items-center justify-between text-[11px] mb-1">
                                <span className="text-muted-foreground">{completed.length} of {route.stops.length} complete</span>
                                <span className="font-mono font-semibold">{progressPct}%</span>
                            </div>
                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Button size="sm" variant="outline" className="flex-1"
                                        onClick={() => setDumpDialogOpen(true)}
                                        data-testid="dump-load-btn">
                                    Dump load
                                </Button>
                                <Button size="sm" className="flex-1"
                                        onClick={finishRoute}
                                        disabled={completed.length === 0}
                                        data-testid="end-route-btn">
                                    End route
                                </Button>
                            </div>
                            {/* List / Map view toggle — segmented control */}
                            <div className="mt-3 grid grid-cols-2 gap-1 p-1 bg-secondary rounded-md"
                                 data-testid="driver-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded transition-colors ${
                                        viewMode === "list"
                                            ? "bg-background shadow-sm text-foreground"
                                            : "text-muted-foreground"
                                    }`}
                                    data-testid="driver-view-list-btn"
                                    aria-pressed={viewMode === "list"}
                                >
                                    <ListIcon className="w-3.5 h-3.5" />List
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode("map")}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded transition-colors ${
                                        viewMode === "map"
                                            ? "bg-background shadow-sm text-foreground"
                                            : "text-muted-foreground"
                                    }`}
                                    data-testid="driver-view-map-btn"
                                    aria-pressed={viewMode === "map"}
                                >
                                    <MapIcon className="w-3.5 h-3.5" />Map
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Dump load modal */}
            {dumpDialogOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
                     onClick={() => setDumpDialogOpen(false)}
                     data-testid="dump-load-modal-backdrop">
                    <div className="bg-background w-full max-w-sm rounded-xl p-5 shadow-2xl"
                         onClick={(e) => e.stopPropagation()}
                         data-testid="dump-load-modal">
                        <div className="font-heading text-lg font-semibold mb-3">Record dump load</div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Weight (tons)</label>
                                <input type="number" step="0.01" value={dumpWeight}
                                       onChange={(e) => setDumpWeight(e.target.value)}
                                       placeholder="4.25"
                                       inputMode="decimal"
                                       className="w-full h-11 px-3 rounded-md border border-input bg-background"
                                       data-testid="dump-weight-input" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Cost ($)</label>
                                <input type="number" step="0.01" value={dumpCost}
                                       onChange={(e) => setDumpCost(e.target.value)}
                                       placeholder="85.00"
                                       inputMode="decimal"
                                       className="w-full h-11 px-3 rounded-md border border-input bg-background"
                                       data-testid="dump-cost-input" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Facility (optional)</label>
                                <input value={dumpFacility}
                                       onChange={(e) => setDumpFacility(e.target.value)}
                                       placeholder="Williamson County Landfill"
                                       className="w-full h-11 px-3 rounded-md border border-input bg-background"
                                       data-testid="dump-facility-input" />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button variant="outline" className="flex-1"
                                        onClick={() => setDumpDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button className="flex-1" onClick={recordDumpLoad}
                                        data-testid="dump-save-btn">
                                    Save load
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground text-center">
                                Made a second dump trip today? Just tap Dump load again.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Stop list */}
            <div className="px-4 py-4 space-y-3 max-w-2xl mx-auto"
                 style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6rem)" }}
                 data-testid="driver-stops">
                <InstallPrompt />

                {/* Map view — full-route pin overview. Non-interactive pins,
                    colored by status, numbered by route order. */}
                {route && viewMode === "map" && (
                    <div className="space-y-2" data-testid="driver-map-view">
                        <RouteStopsMap
                            stops={route.stops || []}
                            className="w-full h-[65vh] rounded-lg border border-border overflow-hidden bg-secondary/20"
                        />
                        <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] text-muted-foreground"
                             data-testid="driver-map-legend">
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600" />Pending
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />Completed
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" />Cart not out
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />Missed
                            </span>
                        </div>
                    </div>
                )}

                {/* Search bar */}
                {viewMode === "list" && route && (route.stops?.length || 0) > 5 && (
                    <div className="relative" data-testid="driver-search-wrap">
                        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={driverSearch}
                            onChange={(e) => setDriverSearch(e.target.value)}
                            placeholder="Search name or address on this route…"
                            className="w-full h-11 pl-10 pr-9 rounded-md border border-input bg-background text-sm"
                            data-testid="driver-search-input"
                        />
                        {driverSearch && (
                            <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                                    onClick={() => setDriverSearch("")}
                                    data-testid="driver-search-clear">
                                <span className="text-lg leading-none">×</span>
                            </button>
                        )}
                    </div>
                )}
                {viewMode === "list" && pending.length === 0 && completed.length > 0 && (
                    <div className="text-center py-8">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <h2 className="font-heading text-xl font-bold">Route complete</h2>
                        <p className="text-sm text-muted-foreground mt-1">Great work today.</p>
                    </div>
                )}

                {viewMode === "list" && (
                <>
                {/* One-tap "Suggested next stop" — big card highlighting the physically
                    closest pending stop. Only shows when GPS is available, we have
                    coords for the nearest stop, and there's more than one pending
                    (single stop is already prominent enough in the list below). */}
                {pending.length > 1 && gpsAvailable && displayPending[0]?._distance != null && (
                    <Card className="border-2 border-primary/60 bg-primary/5 shadow-md"
                          data-testid="driver-suggested-next">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary font-mono">
                                <Navigation className="w-3 h-3" />You&apos;re closest to
                            </div>
                            <div>
                                <div className="font-heading text-xl font-bold leading-tight">
                                    {displayPending[0].customer_name}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" />{displayPending[0].address}
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-xs">
                                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                                        <Navigation className="w-3 h-3" />
                                        {displayPending[0]._distance < 0.1
                                            ? `${Math.round(displayPending[0]._distance * 5280)} ft`
                                            : `${displayPending[0]._distance.toFixed(1)} mi away`}
                                    </span>
                                    {displayPending[0].eta && (
                                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                                            <Clock className="w-3 h-3" />ETA {formatEta(displayPending[0].eta)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                <Button variant="outline" size="lg"
                                        onClick={() => setActiveStop(displayPending[0])}
                                        data-testid="driver-suggested-open"
                                        className="h-14">
                                    <Navigation className="w-5 h-5" />
                                </Button>
                                <Button variant="outline" size="lg"
                                        onClick={() => attachPhotoToStop(displayPending[0])}
                                        data-testid="driver-suggested-photo"
                                        title={displayPending[0].photo_url ? "Replace photo" : "Take photo of cart"}
                                        className="h-14">
                                    <Camera className="w-5 h-5" />
                                    {displayPending[0].photo_url && (
                                        <span className="ml-1 text-[10px] text-blue-600 dark:text-blue-400">✓</span>
                                    )}
                                </Button>
                                <Button size="lg"
                                        onClick={() => markStopComplete(displayPending[0])}
                                        data-testid="driver-suggested-complete"
                                        className="h-14 col-span-2 text-base font-semibold">
                                    <CheckCircle2 className="w-5 h-5 mr-2" />Complete stop
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {pending.length > 0 && (
                    <div className="flex items-center justify-between px-1" data-testid="driver-sort-controls">
                        <div className="overline">
                            Pending · {pending.length}
                            {sortByNearest && gpsAvailable && (
                                <span className="ml-2 text-primary normal-case tracking-normal text-[10px]">by nearest</span>
                            )}
                        </div>
                        {gpsAvailable ? (
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none"
                                       title="Buzz + chime when you get within 200 ft of a stop">
                                    <span className="text-muted-foreground">Alerts</span>
                                    <Switch
                                        checked={arrivalAlertsEnabled}
                                        onCheckedChange={setArrivalAlertsEnabled}
                                        data-testid="driver-arrival-alerts-toggle"
                                    />
                                </label>
                                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                                    <span className="text-muted-foreground">Nearest first</span>
                                    <Switch
                                        checked={sortByNearest}
                                        onCheckedChange={setSortByNearest}
                                        data-testid="driver-sort-nearest-toggle"
                                    />
                                </label>
                            </div>
                        ) : (
                            <div className="text-[10px] italic"
                                 data-testid="driver-no-gps-note">
                                {gpsError === "denied" ? (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-red-600">Location blocked — "nearest me" needs GPS</span>
                                        <button
                                            type="button"
                                            data-testid="driver-gps-retry"
                                            onClick={requestGps}
                                            className="underline decoration-dotted"
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : gpsError === "unavailable" ? (
                                    <span className="text-amber-600">Location unavailable on this device</span>
                                ) : gpsError === "timeout" ? (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-amber-600">Waiting for GPS fix…</span>
                                        <button
                                            type="button"
                                            data-testid="driver-gps-retry"
                                            onClick={requestGps}
                                            className="underline decoration-dotted"
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-muted-foreground">Getting your location…</span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {canReorder && pending.length > 1 && (
                    <div className="text-[11px] text-muted-foreground text-center py-1"
                         data-testid="driver-drag-hint">
                        Long-press the handle to drag a stop into a new order
                    </div>
                )}

                <DndContext
                    sensors={dragSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                >
                    <SortableContext
                        items={displayPending.map((s) => s.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {displayPending.map((stop, idx) => (
                            <SortableStopCard
                                key={stop.id}
                                stop={stop}
                                idx={idx}
                                canReorder={canReorder}
                                onOpen={() => setActiveStop(stop)}
                                onComplete={() => markStopComplete(stop)}
                                onCartNotOut={() => markCartNotOut(stop)}
                                onLogFee={() => logExtraCharge(stop)}
                                onAttachPhoto={() => attachPhotoToStop(stop)}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                {completed.length > 0 && (
                    <>
                        <div className="overline pt-4 pb-1">Completed · {completed.length}</div>
                        {completed.map((stop) => (
                            <div key={stop.id}
                                 className="flex items-center gap-3 p-3 rounded-md border border-border bg-emerald-500/5"
                                 data-testid={`driver-completed-stop-${stop.id}`}>
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{stop.customer_name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{stop.address}</div>
                                    {stop.completed_by_name && stop.completed_by_id !== user?.id && (
                                        <div className="text-[10px] text-emerald-700 dark:text-emerald-400 truncate mt-0.5"
                                             data-testid={`driver-completed-by-${stop.id}`}>
                                            ✓ by {stop.completed_by_name}
                                        </div>
                                    )}
                                    {stop.photo_url && (
                                        <a href={stop.photo_url} target="_blank" rel="noreferrer"
                                           className="text-[10px] text-blue-600 dark:text-blue-400 underline">
                                            Photo attached
                                        </a>
                                    )}
                                </div>
                                <Button size="sm" variant="ghost"
                                        className="text-blue-600 h-8 px-2"
                                        onClick={() => attachPhotoToStop(stop)}
                                        title={stop.photo_url ? "Replace photo" : "Add photo (helper's phone, etc.)"}
                                        data-testid={`driver-attach-photo-${stop.id}`}>
                                    <Camera className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost"
                                        className="text-amber-700 dark:text-amber-400 h-8 px-2 text-[11px]"
                                        onClick={() => uncompleteStop(stop)}
                                        title="Undo — send this stop back to pending"
                                        data-testid={`driver-uncomplete-${stop.id}`}>
                                    Undo
                                </Button>
                            </div>
                        ))}
                    </>
                )}

                {missed.length > 0 && (
                    <>
                        <div className="overline pt-4 pb-1">Missed · {missed.length}</div>
                        {missed.map((stop) => (
                            <div key={stop.id} className="flex items-center gap-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/10">
                                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{stop.customer_name}</div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
                {rejected.length > 0 && (
                    <>
                        <div className="overline pt-4 pb-1">Rejected · {rejected.length}</div>
                        {rejected.map((stop) => (
                            <div key={stop.id} className="flex items-start gap-3 p-3 rounded-md border border-red-500/30 bg-red-500/10">
                                <Ban className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{stop.customer_name}</div>
                                    {stop.rejection_reason && (
                                        <div className="text-[11px] text-red-700/80 mt-0.5 line-clamp-2">{stop.rejection_reason}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </>
                )}
                </>
                )}

                {!route && (
                    <div className="text-center py-16 text-sm text-muted-foreground">
                        <RefreshCw className="w-8 h-8 mx-auto mb-3 opacity-40" />
                        No route assigned for today.
                    </div>
                )}

                <div className="pt-6 pb-2 flex justify-center">
                    <AppVersionBadge />
                </div>
            </div>

            {/* Active stop sheet — map + camera + signature */}
            <Sheet open={!!activeStop} onOpenChange={(open) => { if (!open) resetStopForm(); }}>
                <SheetContent side="bottom" className="h-[95vh] overflow-y-auto p-0" data-testid="driver-stop-sheet">
                    {activeStop && (
                        <>
                            <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
                                <SheetTitle className="font-heading text-xl">{activeStop.customer_name}</SheetTitle>
                                <SheetDescription className="flex items-center gap-1 text-xs">
                                    <MapPin className="w-3 h-3" />{activeStop.address}
                                </SheetDescription>
                            </SheetHeader>

                            <div className="aspect-[16/10] w-full">
                                <GoogleMapDirections
                                    className="w-full h-full"
                                    origin={gps}
                                    destination={destFromStop(activeStop, routes)}
                                    testid="driver-map"
                                />
                            </div>

                            <div className="p-5 space-y-4">
                                <div>
                                    <div className="overline mb-2 flex items-center justify-between">
                                        <span>Photos of pickup {photoPreviews.length > 0 && `(${photoPreviews.length}/5)`}</span>
                                        {photoPreviews.length > 0 && photoPreviews.length < 5 && (
                                            <span className="text-[10px] text-muted-foreground normal-case">Tap + to add more</span>
                                        )}
                                    </div>
                                    <input type="file" accept="image/*" multiple ref={fileInputRef}
                                           onChange={handlePhoto} className="hidden" data-testid="driver-photo-input" />
                                    {photoPreviews.length > 0 ? (
                                        <div className="grid grid-cols-3 gap-2">
                                            {photoPreviews.map((src, idx) => (
                                                <div key={idx} className="relative aspect-square rounded-md overflow-hidden border border-border">
                                                    <img src={src} alt={`capture ${idx + 1}`} className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => clearPhotoAt(idx)}
                                                        data-testid={`driver-photo-clear-${idx}`}
                                                        className="absolute top-1 right-1 bg-background/90 rounded-full p-1 shadow"
                                                        aria-label="Remove photo"
                                                    >
                                                        <Trash className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                            {photoPreviews.length < 5 && (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    data-testid="driver-photo-add-more"
                                                    className="aspect-square rounded-md border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-secondary/40"
                                                >
                                                    <Plus className="w-5 h-5" />
                                                    <span className="text-[10px] mt-1">Add photo</span>
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <Button variant="outline" onClick={() => fileInputRef.current?.click()}
                                                className="w-full h-24 border-dashed" data-testid="driver-photo-btn">
                                            <Camera className="w-5 h-5 mr-2" />Take photos or choose from gallery
                                        </Button>
                                    )}
                                </div>

                                <SignaturePad ref={signatureRef} label="Customer signature (optional)" />

                                <div className="grid grid-cols-2 gap-3">
                                    <label className="flex items-center justify-between p-3 border border-border rounded-md cursor-pointer hover:bg-secondary/40"
                                           htmlFor="contamination-switch">
                                        <div>
                                            <div className="text-sm font-medium flex items-center gap-1">
                                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />Contamination
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">Non-trash items in cart</div>
                                        </div>
                                        <Switch id="contamination-switch" checked={contamination}
                                                onCheckedChange={setContamination}
                                                data-testid="driver-contamination-switch" />
                                    </label>
                                    <label className="flex items-center justify-between p-3 border border-border rounded-md cursor-pointer hover:bg-secondary/40"
                                           htmlFor="damaged-switch">
                                        <div>
                                            <div className="text-sm font-medium flex items-center gap-1">
                                                <Trash className="w-3.5 h-3.5 text-red-500" />Damaged cart
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">Wheel / lid / body broken</div>
                                        </div>
                                        <Switch id="damaged-switch" checked={damagedCart}
                                                onCheckedChange={setDamagedCart}
                                                data-testid="driver-damaged-switch" />
                                    </label>
                                </div>

                                <div>
                                    <div className="overline mb-2">Notes {" "}
                                        <span className="normal-case text-[10px] text-muted-foreground">(also used as the reason if you Reject)</span>
                                    </div>
                                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                                              placeholder="Anything dispatch should know…"
                                              rows={2}
                                              data-testid="driver-notes" />
                                </div>

                                <div className="grid grid-cols-3 gap-2 pt-2">
                                    <Button variant="outline" onClick={() => markMissed(activeStop)}
                                            disabled={photoUploading} data-testid="driver-mark-missed">
                                        <AlertTriangle className="w-4 h-4 mr-1" />Missed
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => markRejected(activeStop)}
                                        disabled={photoUploading}
                                        data-testid="driver-mark-rejected"
                                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    >
                                        <Ban className="w-4 h-4 mr-1" />Reject
                                    </Button>
                                    <Button onClick={() => markStopComplete(activeStop)}
                                            disabled={photoUploading} data-testid="driver-mark-complete">
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        {photoUploading ? "Saving…" : "Complete"}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            {/* Fuel entry sheet */}
            <Sheet open={fuelDrawer} onOpenChange={setFuelDrawer}>
                <SheetContent side="bottom" className="h-auto max-h-[85vh] overflow-y-auto" data-testid="driver-fuel-sheet">
                    <SheetHeader>
                        <SheetTitle className="font-heading text-xl flex items-center gap-2">
                            <Fuel className="w-5 h-5 text-orange-500" />Log fuel
                        </SheetTitle>
                        <SheetDescription>
                            {route?.truck_name ? `Attaches to ${route.truck_name} · today` : "Pick a route first so we can tag the truck"}
                        </SheetDescription>
                    </SheetHeader>
                    <div className="pt-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Total ($)</label>
                                <input type="number" step="0.01" inputMode="decimal"
                                       value={fuelForm.amount}
                                       onChange={(e) => setFuelForm({ ...fuelForm, amount: e.target.value })}
                                       placeholder="0.00"
                                       className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border"
                                       data-testid="driver-fuel-amount" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Gallons</label>
                                <input type="number" step="0.01" inputMode="decimal"
                                       value={fuelForm.gallons}
                                       onChange={(e) => setFuelForm({ ...fuelForm, gallons: e.target.value })}
                                       className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border"
                                       data-testid="driver-fuel-gallons" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Odometer (mi)</label>
                                <input type="number" inputMode="numeric"
                                       value={fuelForm.odometer}
                                       onChange={(e) => setFuelForm({ ...fuelForm, odometer: e.target.value })}
                                       className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border"
                                       data-testid="driver-fuel-odo" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Vendor</label>
                                <input type="text" value={fuelForm.vendor}
                                       onChange={(e) => setFuelForm({ ...fuelForm, vendor: e.target.value })}
                                       placeholder="Shell, Sunoco…"
                                       className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border"
                                       data-testid="driver-fuel-vendor" />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs text-muted-foreground">Receipt photo</label>
                            <input type="file" accept="image/*" capture="environment" ref={fuelFileRef}
                                   onChange={(e) => {
                                       const f = e.target.files?.[0];
                                       if (!f) return;
                                       if (online) runFuelOcr(f);
                                       else setFuelForm({ ...fuelForm, receipt: f });
                                   }}
                                   className="hidden" data-testid="driver-fuel-photo-input" />
                            {fuelForm.receipt ? (
                                <div className="mt-1 space-y-1">
                                    <div className="flex items-center justify-between px-3 py-2 border border-border rounded-md text-sm">
                                        <span className="truncate flex items-center gap-2">
                                            {fuelOcrBusy ? <Sparkles className="w-3.5 h-3.5 animate-pulse text-violet-500" /> : <Camera className="w-3.5 h-3.5 text-muted-foreground" />}
                                            {fuelOcrBusy ? "Reading receipt…" : fuelForm.receipt.name}
                                        </span>
                                        <Button size="sm" variant="ghost"
                                                disabled={fuelOcrBusy}
                                                onClick={() => { setFuelForm({ ...fuelForm, receipt: null }); setFuelOcrHint(null); }}>Clear</Button>
                                    </div>
                                    {fuelOcrHint && (
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                            <Sparkles className="w-3 h-3 text-violet-500" />
                                            AI auto-filled from receipt ({fuelOcrHint} confidence) — please double-check.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button variant="outline" onClick={() => fuelFileRef.current?.click()}
                                        className="w-full h-20 mt-1 border-dashed" data-testid="driver-fuel-photo-btn">
                                    <Camera className="w-5 h-5 mr-2" />Snap receipt
                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-300 uppercase tracking-wider">AI</span>
                                </Button>
                            )}
                        </div>

                        <div className="flex items-center gap-3 pt-4 border-t border-border">
                            <Button variant="outline" className="flex-1" onClick={() => setFuelDrawer(false)}>Cancel</Button>
                            <Button className="flex-1" onClick={submitFuel}
                                    disabled={fuelSaving || !online || !route}
                                    data-testid="driver-fuel-submit">
                                {fuelSaving ? "Saving…" : !route ? "Pick route first" : "Log fuel"}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}

function dataUrlToBlob(dataUrl) {
    const [meta, b64] = dataUrl.split(",");
    const mime = meta.match(/data:(.*?);/)?.[1] || "image/png";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
}

function formatEta(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

function destFromStop(stop, routes) {
    // The stop on the route doesn't currently carry lat/lng — pull from customer address coords
    // Demo: use a sensible default if missing
    if (stop.lat && stop.lng) return { lat: stop.lat, lng: stop.lng };
    // CT shoreline center as a fallback so the map still renders
    return { lat: 41.32, lng: -72.35 };
}


/** A pending stop card wrapped in dnd-kit's `useSortable`. When drag is
 *  disabled (nearest-first mode or active search), the grip button is
 *  hidden entirely and the card renders like a normal card.
 */
function SortableStopCard({ stop, idx, canReorder, onOpen, onComplete, onCartNotOut, onLogFee, onAttachPhoto }) {
    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: stop.id, disabled: !canReorder });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.15)" : undefined,
        touchAction: canReorder ? "manipulation" : undefined,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Card data-testid={`driver-stop-${stop.id}`}>
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                            {canReorder && (
                                <button
                                    {...attributes}
                                    {...listeners}
                                    className="mt-1 -ml-1 p-1.5 rounded touch-none text-muted-foreground hover:text-foreground hover:bg-secondary active:bg-secondary active:cursor-grabbing"
                                    aria-label="Drag to reorder"
                                    data-testid={`driver-drag-${stop.id}`}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <GripVertical className="w-5 h-5" />
                                </button>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-mono text-muted-foreground mb-0.5 flex items-center gap-2 flex-wrap">
                                    <span>Stop #{idx + 1}</span>
                                    {stop._distance != null && (
                                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"
                                              data-testid={`driver-distance-${stop.id}`}>
                                            <Navigation className="w-3 h-3" />
                                            {stop._distance < 0.1
                                                ? `${Math.round(stop._distance * 5280)} ft`
                                                : `${stop._distance.toFixed(1)} mi`}
                                        </span>
                                    )}
                                    {stop.eta && (
                                        <span className="inline-flex items-center gap-1 text-primary" data-testid={`driver-eta-${stop.id}`}>
                                            <Clock className="w-3 h-3" />
                                            {formatEta(stop.eta)}
                                        </span>
                                    )}
                                    {stop.photo_url && (
                                        <a href={stop.photo_url} target="_blank" rel="noreferrer"
                                           className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400"
                                           data-testid={`driver-photo-attached-${stop.id}`}>
                                            <Camera className="w-3 h-3" />photo
                                        </a>
                                    )}
                                </div>
                                <div className="font-heading text-base font-bold truncate">{stop.customer_name}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <MapPin className="w-3 h-3" /> {stop.address}
                                </div>
                            </div>
                        </div>
                        <StatusPill status={stop.status} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <Button variant="outline" size="sm" onClick={onOpen}
                                data-testid={`driver-open-${stop.id}`}>
                            <Navigation className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={onAttachPhoto}
                                data-testid={`driver-photo-${stop.id}`}
                                title={stop.photo_url ? "Replace photo" : "Take photo of cart"}>
                            <Camera className="w-4 h-4" />
                            {stop.photo_url && (
                                <span className="ml-1 text-[10px] text-blue-600 dark:text-blue-400">✓</span>
                            )}
                        </Button>
                        <Button size="sm" onClick={onComplete}
                                data-testid={`driver-quick-complete-${stop.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" />Complete
                        </Button>
                    </div>
                    <Button variant="outline" size="sm"
                            className="w-full text-xs border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                            onClick={onCartNotOut}
                            data-testid={`driver-cart-not-out-${stop.id}`}>
                        <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                        Cart not out
                    </Button>
                    <Button variant="ghost" size="sm" className="w-full text-xs"
                            onClick={onLogFee}
                            data-testid={`driver-extra-charge-${stop.id}`}>
                        <PlusCircle className="w-3.5 h-3.5 mr-1" />
                        Log extra fee (bag / bulk / contamination)
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
